import './bootstrap'

import * as Sentry from '@sentry/vue'
import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import { i18n, loadLocaleMessages, type SupportedLocale } from './i18n'
import { useThemeStore } from './stores/theme'
import { registerSw } from './utils/registerSw'
import { useUserStore } from './stores/user'
import { loadSeoSettings, updateSeo } from './composables/useSeo'

async function bootstrap() {
    // Load the detected locale before mounting so the UI renders in the correct language
    const locale = i18n.global.locale.value as SupportedLocale
    await loadLocaleMessages(locale)

    const app = createApp(App)
    const pinia = createPinia()

    // Initialize Sentry before mounting the app so the Vue error handler and
    // router tracing integrations are active from the first navigation.
    // When VITE_SENTRY_DSN is not set, Sentry is disabled (no-op).
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,
      enabled: Boolean(import.meta.env.VITE_SENTRY_DSN),
      integrations: [
        Sentry.browserTracingIntegration({ router }),
      ],
      tracesSampleRate: parseFloat(String(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1')),
      environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
      release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    })

    app.use(pinia)
    app.use(router)
    app.use(i18n)

    // Attach authenticated user context to Sentry so errors are associated with
    // the correct player. Only non-sensitive fields (id, username) are sent.
    const userStore = useUserStore()
    watch(
      () => userStore.user,
      (user) => {
        if (user) {
          Sentry.setUser({ id: String(user.id), username: user.username })
        } else {
          Sentry.setUser(null)
        }
      },
      { immediate: true },
    )

    // Initialize theme system — applies saved mode and loads API overrides
    const themeStore = useThemeStore()
    themeStore.loadFromApi()

    // Load site SEO settings (site name, description, OG image)
    await loadSeoSettings()

    // Update document title on every navigation using route meta.title
    router.afterEach((to) => {
      const pageTitle = to.meta.title as string | undefined
      updateSeo(pageTitle)
    })

    app.mount('#app')

    // Register push notification service worker
    registerSw()
}

bootstrap()
